/**
 * MobileTrainingReturnDialog — presentational prompt shown when a user returns
 * to OptiBay after an UNVERIFIED mobile training launch.
 *
 * It offers an honest, low-confidence "time away from OptiBay" estimate and the
 * SAME four completion options as the desktop dialog. It NEVER claims verified
 * watch time and NEVER auto-completes — only the consumer's `onCompleted` path
 * persists a (self-attested, unverified) completion. Pure UI; all wiring lives in
 * MobileTrainingReturnPrompt.
 */
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export interface MobileTrainingReturnDialogProps {
  open: boolean;
  onClose: () => void;
  /** Bare "time away" quantity, e.g. "7 minutes" — copy supplies "about". */
  awayText: string;
  durationKnown: boolean;
  /** Bare duration quantity, e.g. "10 minutes"; null when unknown. */
  durationText: string | null;
  estimatedCompletionPct: number | null;
  onCompleted: () => void;
  onPartial: () => void;
  onNotYet: () => void;
  onSkip: () => void;
  onCompleteOnDesktop: () => void;
}

export function MobileTrainingReturnDialog({
  open,
  onClose,
  awayText,
  durationKnown,
  durationText,
  estimatedCompletionPct,
  onCompleted,
  onPartial,
  onNotYet,
  onSkip,
  onCompleteOnDesktop,
}: MobileTrainingReturnDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        data-testid="mobile-training-return-dialog"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle data-testid="mobile-training-return-title">
            Did you complete this training?
          </DialogTitle>
          <DialogDescription data-testid="mobile-training-return-body">
            {durationKnown && durationText !== null
              ? `OptiBay could not verify this training automatically because it opened on mobile. You were away for about ${awayText}. This training is about ${durationText}, so OptiBay estimates up to ${estimatedCompletionPct}% possible completion. This is not verified watch time.`
              : `OptiBay could not verify this training automatically because it opened on mobile. You were away for about ${awayText}. OptiBay could not estimate completion because this training does not have a known duration.`}
          </DialogDescription>
        </DialogHeader>

        <p
          className="text-xs text-muted-foreground"
          data-testid="mobile-training-return-followup"
        >
          Your answer is self-reported and not Companion/provider verified. Your
          shop may review or require desktop Companion-verified completion later.
        </p>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button
            type="button"
            onClick={onCompleted}
            data-testid="mobile-training-return-completed-button"
          >
            Yes, completed in full
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onPartial}
            data-testid="mobile-training-return-partial-button"
          >
            Partially completed
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onNotYet}
            data-testid="mobile-training-return-notyet-button"
          >
            I did not get to it yet
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onSkip}
            data-testid="mobile-training-return-skip-button"
          >
            Not relevant / skip
          </Button>
          <Button
            type="button"
            variant="link"
            className="h-auto p-0 text-xs text-muted-foreground"
            onClick={onCompleteOnDesktop}
            data-testid="mobile-training-return-desktop-button"
          >
            Complete on desktop instead
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
